import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isDatabaseUnavailableError } from '../../shared/localDb';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { awaitSessionBootstrap } from '../session/hydrateSessionCachesFromBootstrap';
import {
  isGlobalFeatureFlagsCacheFresh,
  primeGlobalFeatureFlagsCache,
} from '../feature-flags/globalFeatureFlagsService';
import { fetchGlobalFeatureFlags } from '../feature-flags/globalFeatureFlagsService';
import { subscribeSessionCacheBroadcast } from '../../shell/session/sessionCacheBroadcast';
import type { GlobalFeatureFlagsMap } from '../feature-flags/domain/globalFeatureFlags';
import { WEB_ONLINE_WRITES_FLAG_POLL_MS } from '../feature-flags/webOnlineWrites';
import { Platform } from 'react-native';

type RefreshFeatureFlagsOptions = {
  force?: boolean;
};

type GlobalFeatureFlagsContextValue = {
  flags: GlobalFeatureFlagsMap | null;
  loading: boolean;
  refresh: (options?: RefreshFeatureFlagsOptions) => Promise<void>;
  isStale: boolean;
};

const GlobalFeatureFlagsContext = createContext<GlobalFeatureFlagsContextValue | null>(null);

export function GlobalFeatureFlagsProvider({ children }: { children: React.ReactNode }) {
  const session = useAuthStore((s) => s.session);
  const [flags, setFlags] = useState<GlobalFeatureFlagsMap | null>(null);
  const [loading, setLoading] = useState(true);
  const [isStale, setIsStale] = useState(false);
  const initialFetchDoneRef = useRef(false);

  useEffect(() => {
    if (!session?.user?.id) {
      initialFetchDoneRef.current = false;
    }
  }, [session?.user?.id]);

  useEffect(() => {
    return subscribeSessionCacheBroadcast((message) => {
      if (message.type !== 'feature_flags') return;
      primeGlobalFeatureFlagsCache(message.payload);
      setFlags(message.payload);
      setIsStale(false);
      initialFetchDoneRef.current = true;
      setLoading(false);
    });
  }, []);

  const refresh = useCallback(async (options?: RefreshFeatureFlagsOptions) => {
    const currentSession = useAuthStore.getState().session;
    if (!currentSession) {
      setFlags(null);
      setLoading(false);
      setIsStale(false);
      initialFetchDoneRef.current = false;
      return;
    }
    const uid = currentSession.user.id;
    const force = options?.force === true;
    if (!force && initialFetchDoneRef.current && isGlobalFeatureFlagsCacheFresh()) {
      return;
    }
    if (!initialFetchDoneRef.current) {
      setLoading(true);
    }
    try {
      await awaitSessionBootstrap(uid);
      const row = await fetchGlobalFeatureFlags({ force });
      if (useAuthStore.getState().session?.user?.id !== uid) return;
      initialFetchDoneRef.current = true;
      if (row) {
        setFlags(row);
        setIsStale(false);
      } else {
        setFlags(null);
        setIsStale(true);
      }
    } catch (e) {
      if (useAuthStore.getState().session?.user?.id !== uid) return;
      if (!isDatabaseUnavailableError(e)) {
        console.warn('[GlobalFeatureFlagsProvider] fetch', e);
      }
      setFlags(null);
      setIsStale(true);
    } finally {
      if (useAuthStore.getState().session?.user?.id === uid) {
        setLoading(false);
      }
    }
  }, [session?.user?.id]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await refresh();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    const onVisible = () => {
      // Sin force: el TTL (24h) ya define la frescura; forzar en cada
      // visibilitychange hacía un RPC por alt+tab.
      if (document.visibilityState === 'visible') {
        void refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void refresh({ force: true });
    }, WEB_ONLINE_WRITES_FLAG_POLL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, [refresh]);

  const value = useMemo(
    () => ({
      flags,
      loading,
      refresh,
      isStale,
    }),
    [flags, loading, refresh, isStale]
  );

  return <GlobalFeatureFlagsContext.Provider value={value}>{children}</GlobalFeatureFlagsContext.Provider>;
}

export function useGlobalFeatureFlags(): GlobalFeatureFlagsContextValue {
  const ctx = useContext(GlobalFeatureFlagsContext);
  if (!ctx) {
    throw new Error('useGlobalFeatureFlags debe usarse dentro de GlobalFeatureFlagsProvider');
  }
  return ctx;
}
